import Component from '@glimmer/component';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import t from 'ember-intl/helpers/t';
import type { IntlService } from 'ember-intl';
import type Store from '@ember-data/store';
import AlertBanner from 'tio-ui/components/alert-banner';
import Checkbox from 'tio-ui/components/checkbox';
import Chip from 'tio-ui/components/chip';
import Popover from 'tio-ui/components/popover';
import Table from 'tio-ui/components/table';
import { Section } from 'tio-ui/components/layout';
import { PhArrowUp, PhInfo } from 'ember-phosphor-icons';
import MultiLineChart from 'tio-ui/components/charts/multi-line-chart';
import type { MultiLineSeries } from 'tio-ui/components/charts/multi-line-chart';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import {
  generatePaymentPlanForUserRequest,
  type GeneratePaymentPlanResponse,
  type PaymentPlanRow,
} from 'tio-common/requests/payment-plan-explorer';
import {
  buildGeneratePayload,
  isCurrentPlanRow,
  scheduleMonthLabels,
  type Sf0726ResultSection,
  type SortedPlanRow,
} from 'tio-employee/utils/strategy-finder-0726';

interface LoanPlansSectionSignature {
  Args: {
    section: Sf0726ResultSection;
    // The analysis date (recommendation createdAt) the schedules start from, so
    // the graph x-axis reads as Month/Year rather than a payment index.
    analysisDate?: Date | string;
  };
  Element: HTMLElement;
}

// Distinct line colors for the comparison graph (balance solid, payment dashed).
const PALETTE = ['#2563eb', '#16a34a', '#d97706', '#9333ea', '#dc2626', '#0891b2'];

const rowKey = (row: SortedPlanRow) => `${row.plan}::${row.tlf_amount}::${row.pslf}`;

export default class LoanPlansSection extends Component<LoanPlansSectionSignature> {
  @service declare intl: IntlService;
  @service declare store: Store;

  @tracked selectedRows: SortedPlanRow[] = [];
  @tracked scheduleByKey: Record<string, PaymentPlanRow[]> = {};
  @tracked failedKeys: string[] = [];

  // Keys with a schedule fetch in flight. `fetchSchedule` is a plain `task` (not
  // `dropTask`) on purpose: each selected plan fetches its own schedule and they
  // must run concurrently, so dropping in-flight performs would silently starve
  // later selections. This set instead guards only against re-fetching the SAME
  // key — e.g. a fast off→on toggle before the first fetch resolves.
  #pendingKeys = new Set<string>();

  get plans() {
    return this.args.section.plans;
  }

  dollars = (cents: number) => cents / 100;

  formatDollars = (value: number) =>
    this.intl.formatNumber(value, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  loanTypeLabel = (loanType: string) => {
    const key = `strategy_finder_0726.loan_types.${loanType}`;
    return this.intl.exists(key) ? this.intl.t(key) : loanType;
  };

  // All display text is local; the API's `title` is intentionally never rendered.
  planLabel = (row: SortedPlanRow) => {
    const key = `strategy_finder_0726.plans.${row.plan}`;
    let label = this.intl.exists(key) ? this.intl.t(key) : row.plan;
    if (row.tlf_amount > 0) {
      const amount = this.formatDollars(this.dollars(row.tlf_amount));
      label = `${label} ${this.intl.t('strategy_finder_0726.results.with_tlf', { amount })}`;
    }
    if (row.pslf) {
      // When the loan is repaid before the PSLF cutoff, PSLF forgives nothing —
      // call that out in the plan name rather than implying a benefit.
      const suffix =
        row.pslf_forgiveness > 0
          ? this.intl.t('strategy_finder_0726.results.with_pslf')
          : this.intl.t('strategy_finder_0726.results.with_pslf_nothing');
      label = `${label} ${suffix}`;
    }
    return label;
  };

  // The PSLF-forgiveness cell: the amount for a PSLF row, or an em dash for a row
  // that doesn't use PSLF (non-income-driven plans, or the current plan when the
  // borrower is eligible but not pursuing).
  pslfCell = (row: SortedPlanRow) =>
    row.pslf ? this.formatDollars(this.dollars(row.pslf_forgiveness)) : '—';

  // TLF column shows whenever the borrower opted into a TLF level; PSLF column
  // shows whenever they are PSLF-eligible. With neither, fall back to a single
  // combined "amount forgiven" column so IDR-term forgiveness still shows.
  get showTlfColumn() {
    return this.plans.some((p) => p.tlf_amount > 0);
  }

  get showPslfColumn() {
    return !!this.args.section.input?.pslf_eligible;
  }

  get showTotalForgivenessColumn() {
    return !this.showTlfColumn && !this.showPslfColumn;
  }

  // The rows are returned pre-sorted by the borrower's chosen key; seed the
  // active sort from it and let the user re-sort (ascending only — there is no
  // value in floating the highest payment to the top).
  @tracked sortColumn: 'first_month' | 'total' =
    this.args.section.sortBy === 'total_then_first_month' ? 'total' : 'first_month';

  setSortColumn = (column: 'first_month' | 'total') => {
    this.sortColumn = column;
  };

  isSortedBy = (column: 'first_month' | 'total') => this.sortColumn === column;

  get sortedPlans() {
    const rows = [...this.plans];
    if (this.sortColumn === 'total') {
      rows.sort(
        (a, b) => a.total_payment - b.total_payment || a.first_month_payment - b.first_month_payment
      );
    } else {
      rows.sort(
        (a, b) => a.first_month_payment - b.first_month_payment || a.total_payment - b.total_payment
      );
    }
    return rows;
  }

  get compareLabel() {
    return this.intl.t('strategy_finder_0726.graph.compare');
  }

  isCurrentPlan = (row: SortedPlanRow) => isCurrentPlanRow(row, this.args.section);

  // Unique per-row test key (plan + TLF + PSLF variant), since the same plan can
  // appear in multiple rows — `data-test-sf-0726-plan-row` (plan only) is ambiguous.
  rowKeyFor = (row: SortedPlanRow) => rowKey(row);

  rowClass = (row: SortedPlanRow) => (this.isCurrentPlan(row) ? 'tio-bg-success-soft' : '');

  isSelected = (row: SortedPlanRow) => this.selectedRows.some((r) => rowKey(r) === rowKey(row));

  toggleRow = (row: SortedPlanRow) => {
    const key = rowKey(row);
    if (this.isSelected(row)) {
      this.selectedRows = this.selectedRows.filter((r) => rowKey(r) !== key);
    } else {
      this.selectedRows = [...this.selectedRows, row];
      if (!this.scheduleByKey[key] && !this.#pendingKeys.has(key)) {
        this.fetchSchedule.perform(row);
      }
    }
  };

  fetchSchedule = task(async (row: SortedPlanRow) => {
    const input = this.args.section.input;
    if (!input) {
      return;
    }
    const key = rowKey(row);
    this.#pendingKeys.add(key);
    this.failedKeys = this.failedKeys.filter((k) => k !== key);
    try {
      const payload = buildGeneratePayload(input, row.plan, row.tlf_amount, row.pslf);
      const { content } = await this.store.request(generatePaymentPlanForUserRequest(payload));
      const data = content as GeneratePaymentPlanResponse;
      if (data.is_error || data.error || !Array.isArray(data.rows) || data.rows.length === 0) {
        // Empty rows would be silently filtered out of the chart, leaving the
        // user with no feedback — surface it as an error instead.
        throw new Error(data.error ?? 'generate returned no schedule');
      }
      this.scheduleByKey = { ...this.scheduleByKey, [key]: data.rows };
    } catch (e) {
      console.error(e);
      this.failedKeys = [...this.failedKeys, key];
    } finally {
      this.#pendingKeys.delete(key);
    }
  });

  get selectedWithData() {
    return this.selectedRows
      .map((row) => ({ row, rows: this.scheduleByKey[rowKey(row)] ?? [] }))
      .filter((entry) => entry.rows.length > 0);
  }

  // Month/Year x-axis labels starting from the analysis date, one per month.
  monthLabels = (count: number): string[] =>
    scheduleMonthLabels(this.args.analysisDate, count, (date) =>
      this.intl.formatDate(date, { month: 'short', year: 'numeric', timeZone: 'UTC' })
    );

  // One chart per selected plan (kept separate rather than overlaid), each with
  // its balance, cumulative total paid, and monthly-payment series. All charts in
  // the section share one x-axis range (the longest payoff, labelled Month/Year
  // from the analysis date) and one y-scale (shared yMax/yMaxRight) so their axis
  // lengths are directly comparable. Each plan's own series is NOT padded, so its
  // lines stop at that plan's payoff rather than running flat to the longest one.
  get charts(): {
    key: string;
    title: string;
    labels: string[];
    yMax: number;
    yMaxRight: number;
    series: MultiLineSeries[];
  }[] {
    const entries = this.selectedWithData;
    if (!entries.length) {
      return [];
    }

    const maxMonths = Math.max(...entries.map((entry) => entry.rows.length));
    const labels = this.monthLabels(maxMonths);

    // Build each plan's own-length series and track the shared axis maxima.
    let leftMax = 0;
    let rightMax = 0;
    const built = entries.map((entry) => {
      const balance = entry.rows.map((r) => r.balance / 100);
      const monthly = entry.rows.map((r) => r.payment / 100);
      // Running cumulative total paid (employee + employer), like the explorer.
      let cumulativeTotal = 0;
      const totalPaid = entry.rows.map((r) => {
        cumulativeTotal += (r.payment + r.employer_payment) / 100;
        return cumulativeTotal;
      });
      leftMax = Math.max(leftMax, ...balance, ...totalPaid);
      rightMax = Math.max(rightMax, ...monthly);
      return { entry, balance, totalPaid, monthly };
    });

    return built.map(({ entry, balance, totalPaid, monthly }) => ({
      key: rowKey(entry.row),
      title: this.planLabel(entry.row),
      // Shared full-range labels; a shorter plan's series simply stops early (the
      // chart draws each series only up to its own data length).
      labels,
      yMax: leftMax,
      yMaxRight: rightMax,
      series: [
        {
          label: this.intl.t('strategy_finder_0726.graph.balance'),
          data: balance,
          color: PALETTE[0] ?? '#2563eb',
        },
        {
          label: this.intl.t('strategy_finder_0726.graph.total_paid'),
          data: totalPaid,
          color: PALETTE[2] ?? '#d97706',
          filled: true,
        },
        {
          label: this.intl.t('strategy_finder_0726.graph.monthly_payment'),
          data: monthly,
          color: PALETTE[1] ?? '#16a34a',
          dashed: true,
          yAxis: 'right',
        },
      ],
    }));
  }

  get hasGraph() {
    return this.charts.length > 0;
  }

  // This loan's TLF forgiveness is a split share of the household amount (only
  // when its balance share multiplier is < 1, i.e. it shares the Direct/FFEL
  // pool with another loan type and TLF is in play).
  get showTlfSplitInfo() {
    const multiplier = this.args.section.input?.multiplier ?? 1;
    return multiplier < 1 && this.plans.some((p) => p.tlf_amount > 0);
  }

  <template>
    <Section data-test-sf-0726-results-section={{@section.loanType}} ...attributes>
      <:header>{{this.loanTypeLabel @section.loanType}}</:header>
      <:body>
        {{#if @section.availablePlansWarning}}
          <AlertBanner @intent="warning" class="mb-3">
            <p class="text-sm">{{t "strategy_finder_0726.results.available_plans_warning"}}</p>
          </AlertBanner>
        {{/if}}

        <Table @isLoading={{false}}>
          <:thead as |options|>
            <options.tr>
              <options.th>{{t "strategy_finder_0726.graph.compare"}}</options.th>
              <options.th>{{t "strategy_finder_0726.results.columns.plan"}}</options.th>
              <options.th>
                <button
                  type="button"
                  class="tio-row items-center gap-1"
                  data-test-sf-0726-sort="first_month"
                  {{on "click" (fn this.setSortColumn "first_month")}}
                >
                  {{t "strategy_finder_0726.results.columns.first_month_payment"}}
                  {{#if (this.isSortedBy "first_month")}}
                    <PhArrowUp @size={{14}} data-test-sf-0726-sort-active />
                  {{/if}}
                </button>
              </options.th>
              <options.th>{{t
                  "strategy_finder_0726.results.columns.max_monthly_payment"
                }}</options.th>
              <options.th>
                <button
                  type="button"
                  class="tio-row items-center gap-1"
                  data-test-sf-0726-sort="total"
                  {{on "click" (fn this.setSortColumn "total")}}
                >
                  {{t "strategy_finder_0726.results.columns.total_payment"}}
                  {{#if (this.isSortedBy "total")}}
                    <PhArrowUp @size={{14}} data-test-sf-0726-sort-active />
                  {{/if}}
                </button>
              </options.th>
              {{#if this.showTlfColumn}}
                <options.th>
                  <span class="tio-row items-center gap-1">
                    {{t "strategy_finder_0726.results.columns.tlf_forgiveness"}}
                    {{#if this.showTlfSplitInfo}}
                      <Popover @triggerMode="hover">
                        <:trigger as |pop|>
                          <span
                            {{pop.register}}
                            {{pop.hoverHandlers}}
                            data-test-sf-0726-tlf-split-info
                          >
                            <PhInfo class="tio-fg-secondary" @size={{14}} />
                          </span>
                        </:trigger>
                        <:body>
                          <p class="text-sm max-w-xs">{{t
                              "strategy_finder_0726.results.tlf_split_info"
                            }}</p>
                        </:body>
                      </Popover>
                    {{/if}}
                  </span>
                </options.th>
              {{/if}}
              {{#if this.showPslfColumn}}
                <options.th>{{t
                    "strategy_finder_0726.results.columns.pslf_forgiveness"
                  }}</options.th>
              {{/if}}
              {{#if this.showTotalForgivenessColumn}}
                <options.th>{{t "strategy_finder_0726.results.columns.forgiveness"}}</options.th>
              {{/if}}
              <options.th>{{t "strategy_finder_0726.results.columns.num_payments"}}</options.th>
            </options.tr>
          </:thead>
          <:tbody as |options|>
            {{#each this.sortedPlans as |row|}}
              <options.tr
                class={{this.rowClass row}}
                data-test-sf-0726-plan-row={{row.plan}}
                data-test-sf-0726-plan-row-key={{this.rowKeyFor row}}
                data-test-sf-0726-current-plan={{this.isCurrentPlan row}}
              >
                <options.td>
                  <Checkbox
                    @checked={{this.isSelected row}}
                    @onChange={{fn this.toggleRow row}}
                    aria-label={{this.compareLabel}}
                  />
                </options.td>
                <options.td>
                  {{this.planLabel row}}
                  {{#if (this.isCurrentPlan row)}}
                    <Chip @size="sm" data-test-sf-0726-current-plan-badge>
                      {{t "strategy_finder_0726.results.current_plan_badge"}}
                    </Chip>
                  {{/if}}
                </options.td>
                <options.td>
                  {{safeFormatNumber
                    (this.dollars row.first_month_payment)
                    style="currency"
                    currency="USD"
                    maximumFractionDigits=0
                  }}
                </options.td>
                <options.td>
                  {{safeFormatNumber
                    (this.dollars row.max_monthly_payment)
                    style="currency"
                    currency="USD"
                    maximumFractionDigits=0
                  }}
                </options.td>
                <options.td>
                  {{safeFormatNumber
                    (this.dollars row.total_payment)
                    style="currency"
                    currency="USD"
                    maximumFractionDigits=0
                  }}
                </options.td>
                {{#if this.showTlfColumn}}
                  <options.td data-test-sf-0726-tlf-cell>
                    {{safeFormatNumber
                      (this.dollars row.tlf_forgiveness)
                      style="currency"
                      currency="USD"
                      maximumFractionDigits=0
                    }}
                  </options.td>
                {{/if}}
                {{#if this.showPslfColumn}}
                  <options.td data-test-sf-0726-pslf-cell>{{this.pslfCell row}}</options.td>
                {{/if}}
                {{#if this.showTotalForgivenessColumn}}
                  <options.td>
                    {{safeFormatNumber
                      (this.dollars row.forgiveness_paid)
                      style="currency"
                      currency="USD"
                      maximumFractionDigits=0
                    }}
                  </options.td>
                {{/if}}
                <options.td>{{row.num_payments}}</options.td>
              </options.tr>
            {{/each}}
          </:tbody>
        </Table>

        <div class="mt-4" data-test-sf-0726-graph>
          {{#if this.hasGraph}}
            {{#each this.charts as |chart|}}
              <div class="mb-6" data-test-sf-0726-graph-plan={{chart.key}}>
                <p class="tio-text-sm-semibold mb-1">{{chart.title}}</p>
                <MultiLineChart
                  @labels={{chart.labels}}
                  @series={{chart.series}}
                  @yMax={{chart.yMax}}
                  @yMaxRight={{chart.yMaxRight}}
                  @formatValue={{this.formatDollars}}
                  @formatValueRight={{this.formatDollars}}
                  @height={{240}}
                />
              </div>
            {{/each}}
          {{else if this.fetchSchedule.isRunning}}
            <p class="text-sm tio-fg-secondary">{{t "strategy_finder_0726.graph.loading"}}</p>
          {{else}}
            <p class="text-sm tio-fg-secondary">{{t "strategy_finder_0726.graph.empty"}}</p>
          {{/if}}
          {{#if this.failedKeys.length}}
            <p class="text-sm tio-fg-danger">{{t "strategy_finder_0726.graph.error"}}</p>
          {{/if}}
        </div>
      </:body>
    </Section>
  </template>
}
